import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/interpreterAccess/dashboard/ListTable";
import { InterpreterDashboardSelector } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAnalytics } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAppointments, downloadDashboardReport } from "slices/interpreter/dashboardSlice";
import { interpreterLegends, LegendButton } from "components/common/TableColor";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import DateRangeFilter from "components/common/DateRangeFilter";
import { fetchSchools } from "slices/schoolSlice";
import { schoolSelector } from "slices/schoolSlice";

// Status options for multi-select dropdown (excludes 'All' — empty selection means All)
const statusOptions = [
  { value: 'open',      label: 'Open' },
  { value: 'assigned',  label: 'Assigned' },
  { value: 'pending',   label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'declined',  label: 'Declined' },
  { value: 'day_off',     label: 'Day Off' },
  { value: 'travel_time', label: 'Travel Time' },
  { value: 'lunch',       label: 'Lunch' },
];

// Statuses shown when clicking a Registered analytics cell
const REGISTERED_STATUSES = ['assigned', 'pending', 'completed', 'cancelled', 'declined', 'day_off'];

function Dashboard() {
  const dispatch = useDispatch();
  const { interpreterDashboard: dashboard, last_page: totalPages, loader } = useSelector(InterpreterDashboardSelector);
  const lunchRecords = dashboard?.lunchRecords ?? [];
  const { language: languages } = useSelector(languageSelector);
  const { school: schools } = useSelector(schoolSelector);
  const [downloading, setDownloading] = useState(false);
  const [languageState, setLanguageState] = useState([]);
  const navigate = useNavigate();
  const tableRef = useRef(null);

  const today = moment().format('YYYY-MM-DD');

  const defaultValues = {
    start_date: new Date(),
    end_date: new Date(),
    appointment_id: "",
    student_name: "",
    requester: "",
    school_id: null,
    type: null,
    language_ids: [],
    status_filter: [], // empty = All
  };

  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, reset, getValues } = useForm({
    defaultValues,
  });
  const baseParams = { pageLength: 100, sortBy: "date", sortDirection: "asc", type: [0, 1, 4, 5] };
  const todayParams = { ...baseParams, start_date: today, end_date: today };
  const [filters, setFilters] = useState(todayParams);

  // showLunch: true when nothing selected (All) or 'lunch' is explicitly selected
  const statusFilter = watch('status_filter');
  const showLunch = !statusFilter?.length || statusFilter.includes('lunch');

  useEffect(() => {
    dispatch(fetchInterpreterDashboardAnalytics());
    dispatch(fetchInterpreterDashboardAppointments(1, todayParams));
  }, [dispatch]);

  // #region Language dropdown
  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result.data.map((item) => ({ value: item.id, label: item.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };
  //#endregion

  // #region School dropdown
  const filteredSchools = React.useMemo(() =>
    schools?.map(s => ({ value: s?.id, label: s?.name })), [schools]);

  const filteredSchoolLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchSchools(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result.data.map((item) => ({ value: item.id, label: item.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };
  //#endregion

  // #region Legends (display only)
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...interpreterLegends];
    temp_array.unshift({ value: "all", name: "All", id: 0, color: "#3176A0" });
    return temp_array;
  }, [interpreterLegends]);
  //#endregion

  // Derive type + status API params from the multi-select status_filter array
  const resolveStatusFilter = (selectedStatuses, formType) => {
    const nonLunch = (selectedStatuses || []).filter(s => s !== 'lunch');
    const hasLunchOnly = (selectedStatuses || []).includes('lunch') && nonLunch.length === 0;

    // If a specific type is chosen in the Type dropdown, honour it; otherwise default to all
    const type = (formType !== null && formType !== undefined) ? [formType] : [0, 1, 4, 5];

    let status;
    if (hasLunchOnly) {
      // Impossible status — no real rows match, so only lunch records (via showLunch) are displayed
      status = ['__lunch_only__'];
    } else if (nonLunch.length > 0) {
      status = nonLunch;
    } else {
      status = undefined; // All — no status filter
    }

    return { type, status };
  };

  // Centralised param builder
  const buildParams = (formValues) => {
    const { type, status } = resolveStatusFilter(formValues.status_filter, formValues.type);
    return {
      ...baseParams,
      start_date: formValues.start_date ? moment(formValues.start_date).format('YYYY-MM-DD') : undefined,
      end_date: formValues.end_date ? moment(formValues.end_date).format('YYYY-MM-DD') : undefined,
      appointment_id: formValues.appointment_id || undefined,
      student_name: formValues.student_name || undefined,
      requester: formValues.requester || undefined,
      school_id: formValues.school_id || undefined,
      type,
      language_ids: formValues.language_ids?.length ? formValues.language_ids : undefined,
      status: status || undefined,
    };
  };

  const onSubmit = (values) => {
    dispatch(setError({}));
    const params = values.appointment_id
      ? { pageLength: 100, sortBy: 'date', sortDirection: 'desc', appointment_id: values.appointment_id }
      : buildParams(values);
    setFilters(params);
    dispatch(fetchInterpreterDashboardAppointments(1, params));
    clearErrors();
  };

  const handleResetFields = () => {
    reset(defaultValues);
    setLanguageState([]);
    setFilters(todayParams);
    dispatch(fetchInterpreterDashboardAppointments(1, todayParams));
    clearErrors();
  };

  const downloadBlobFile = (blob, filename = "appointments.xlsx") => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    setDownloading(true);
    const values = getValues();
    const { type, status } = resolveStatusFilter(values.status_filter, values.type);
    const currentFilters = { ...values, type, status };
    dispatch(downloadDashboardReport(currentFilters, (success, res) => {
      setDownloading(false);
      if (!success) {
        Swal.fire({ title: "Error!", text: (res && res.message) || "Failed to download report", icon: "error", confirmButtonColor: "#d33" });
        return;
      }
      try {
        if (res instanceof Blob) {
          downloadBlobFile(res);
        } else if (res && res.blob) {
          downloadBlobFile(res.blob, res.filename || "dashboard_report.xlsx");
        } else if (typeof res === "string" && res.startsWith("data:")) {
          const arr = res.split(",");
          const mimeMatch = arr[0].match(/:(.*?);/);
          const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8 = new Uint8Array(n);
          while (n--) u8[n] = bstr.charCodeAt(n);
          downloadBlobFile(new Blob([u8], { type: mime }));
        } else if (typeof res === "string") {
          const a = document.createElement("a");
          a.href = res;
          a.target = "_blank";
          a.download = "";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        Swal.fire({ title: "Success!", text: "Report downloaded successfully.", icon: "success", confirmButtonColor: "#3085d6" });
      } catch (err) {
        console.error("Download error:", err);
        Swal.fire({ title: "Error!", text: "Could not process downloaded file", icon: "error", confirmButtonColor: "#d33" });
      }
    }));
  };

  const applyAnalyticFilter = (type, startDate, endDate) => {
    if (type !== 0 && type !== 1) return;
    const start = typeof startDate === 'string' ? startDate : moment(startDate).format('YYYY-MM-DD');
    const end   = typeof endDate   === 'string' ? endDate   : moment(endDate).format('YYYY-MM-DD');
    setValue('start_date', new Date(start));
    setValue('end_date', new Date(end));
    // Registered = all statuses except 'open' (interpreter's own appointments can't be open)
    setValue('status_filter', REGISTERED_STATUSES);
    const params = {
      ...baseParams,
      start_date: start,
      end_date: end,
      type: [type],
      registered_only: true,
    };
    setFilters(params);
    dispatch(fetchInterpreterDashboardAppointments(1, params));
    tableRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Use server-returned dates so the click range exactly matches what the analytics counted
  const todayDate      = dashboard?.analytics?.today_date;
  const fourteenStart  = dashboard?.analytics?.fourteen_start;
  const fourteenEnd    = dashboard?.analytics?.fourteen_end;

  const viewAppointments = (type) => {
    if (type === 0 || type === 1) {
      const date = todayDate || moment().format('YYYY-MM-DD');
      applyAnalyticFilter(type, date, date);
    } else if (type === 2) {
      navigate("/interpreter/billingual-requests", { state: { filterByToday: true } });
    } else if (type === 3) {
      navigate("/interpreter/translations", { state: { filterByToday: true } });
    }
  };

  const nextFourteenDaysAppointments = (type) => {
    if (type === 0 || type === 1) {
      const start = fourteenStart || moment().add(1, 'day').format('YYYY-MM-DD');
      const end   = fourteenEnd   || moment().add(14, 'days').format('YYYY-MM-DD');
      applyAnalyticFilter(type, start, end);
    } else if (type === 2) {
      navigate("/interpreter/billingual-requests", { state: { filterByFourteen: true } });
    } else if (type === 3) {
      navigate("/interpreter/translations", { state: { filterByFourteen: true } });
    }
  };

  const applyToCoverFilter = (type, start, end) => {
    if (type !== 0 && type !== 1) return;
    setValue('start_date', new Date(start));
    setValue('end_date', new Date(end));
    // To Cover = open appointments the interpreter has been invited to
    setValue('status_filter', ['open']);
    const params = {
      ...baseParams,
      start_date: start,
      end_date: end,
      type: [type],
      cover_only: true,
    };
    setFilters(params);
    dispatch(fetchInterpreterDashboardAppointments(1, params));
    tableRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const viewToCoverAppointments = (type, period) => {
    if (type === 0 || type === 1) {
      const start = period === 'today'
        ? (todayDate     || moment().format('YYYY-MM-DD'))
        : (fourteenStart || moment().add(1, 'day').format('YYYY-MM-DD'));
      const end = period === 'today'
        ? (todayDate   || moment().format('YYYY-MM-DD'))
        : (fourteenEnd || moment().add(14, 'days').format('YYYY-MM-DD'));
      applyToCoverFilter(type, start, end);
    } else if (type === 2) {
      navigate("/interpreter/billingual-requests", {
        state: period === 'today' ? { filterByToday: true } : { filterByFourteen: true },
      });
    } else if (type === 3) {
      navigate("/interpreter/translations", {
        state: period === 'today' ? { filterByToday: true } : { filterByFourteen: true },
      });
    }
  };

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>
            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => viewAppointments(service?.value)}>{service?.type}</td>
                      <td className="cursorPointer text-center" onClick={() => viewAppointments(service?.value)}>{service?.assigned}</td>
                      <td className="cursorPointer text-center" onClick={() => viewToCoverAppointments(service?.value, 'today')}>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>
            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => nextFourteenDaysAppointments(service?.value)}>{service?.type}</td>
                      <td className="cursorPointer text-center" onClick={() => nextFourteenDaysAppointments(service?.value)}>{service?.assigned}</td>
                      <td className="cursorPointer text-center" onClick={() => viewToCoverAppointments(service?.value, 'fourteen')}>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card_table">
          {/* Legend — decorative only, not clickable */}
          <div className="button-sm forLegends p-3 gap-1" style={{ pointerEvents: 'none', cursor: 'default' }}>
            <LegendButton activeIds={[]} options={filteredStatuses} handleClick={() => {}} />
            <div className="border_btn">
              <button type="button" className="button-bu" style={{ backgroundColor: '#2e7d32' }}>
                <p>Lunch</p>
              </button>
            </div>
          </div>

          <form className="search_form_ad" onSubmit={handleSubmit(onSubmit)}>
            <div className="row-primary dahboard_filter appointments">
              <DateRangeFilter control={control} errors={errors} />

              <div className="row-primary">
                <h2>Appointment ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter ID" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Student Name</h2>
                <Controller
                  name="student_name"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter Student Name" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Requester</h2>
                <Controller
                  name="requester"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter Requester" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>School</h2>
                <Controller
                  name="school_id"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{ ...selectCustomStyles }}
                        value={field.value ? filteredSchools?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredSchoolLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => field.onChange(obj?.value ?? null)}
                        additional={{ page: 1 }}
                      />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Type</h2>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        styles={selectCustomStyles}
                        options={[
                          { value: 0, label: "Interpreting Request" },
                          { value: 1, label: "After Hours Request" },
                        ]}
                        isClearable
                        placeholder="Select Type"
                        onChange={(option) => field.onChange(option !== null ? option.value : null)}
                        value={
                          field.value !== null && field.value !== undefined
                            ? [
                                { value: 0, label: "Interpreting Request" },
                                { value: 1, label: "After Hours Request" },
                              ].find((opt) => opt.value === field.value)
                            : null
                        }
                      />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Languages</h2>
                <Controller
                  name="language_ids"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }} className="multiselect">
                      <AsyncPaginate
                        menuPlacement="auto"
                        isMulti
                        styles={{ ...selectCustomStyles }}
                        value={languageState}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(selected) => {
                          setLanguageState(selected);
                          field.onChange((selected || []).map((opt) => opt.value));
                        }}
                        additional={{ page: 1 }}
                      />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Status</h2>
                <Controller
                  name="status_filter"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }} className="multiselect">
                      <AsyncPaginate
                        menuPlacement="auto"
                        isMulti
                        styles={{ ...selectCustomStyles }}
                        options={statusOptions}
                        placeholder="All Statuses"
                        isClearable
                        escapeClearsValue
                        onChange={(selected) => {
                          field.onChange((selected || []).map(opt => opt.value));
                        }}
                        value={(field.value || []).map(v => statusOptions.find(opt => opt.value === v)).filter(Boolean)}
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between btns_area">
              <div className="row-primary row_10">
                <SubmitButton buttonType="submit" btnText="Search" className="green" />
                <SubmitButton buttonType="button" className="lightBlue" btnText="Reset" handleSubmit={handleResetFields} />
              </div>
              <button
                type="button"
                style={{ height: "fit-content", marginLeft: "10px" }}
                className="main_button"
                onClick={handleDownloadReport}
                disabled={downloading}
              >
                {downloading ? "Exporting..." : "Export to Excel"}
              </button>
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} lunchRecords={lunchRecords} showLunch={showLunch} filtration={filters} />
            </div>
          </form>
        </div>

      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;
